import { useState, useMemo } from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Checkbox } from '@/components/ui/checkbox'
import { cn } from '@/lib/utils'
import { Check, Loader2, Search } from 'lucide-react'
import { CounterpartEditor, type CounterpartPayload } from './CounterpartEditor'
import { useAllOpenItems, useCreateMatch } from './hooks/useReconApi'
import type { Transaction, Counterpart } from './types'
import { formatCurrency } from './format'

interface MatchTabProps {
  transaction: Transaction
  clientId: string
  bankAccountId: number
  bankAccountLabel: string
}

export function MatchTab({ transaction, clientId, bankAccountId, bankAccountLabel }: MatchTabProps) {
  const { data: openItems = [], isLoading } = useAllOpenItems(clientId)
  const createMatch = useCreateMatch()
  const [selected, setSelected] = useState<Set<number>>(new Set())
  const [search, setSearch] = useState('')

  const filtered = useMemo(() => {
    if (!search) return openItems
    const q = search.toLowerCase()
    return openItems.filter(
      (oi) =>
        oi.account_code?.toLowerCase().includes(q) ||
        oi.account_name?.toLowerCase().includes(q) ||
        oi.doc_number?.toLowerCase().includes(q) ||
        oi.counterparty_name?.toLowerCase().includes(q) ||
        oi.balance?.includes(q)
    )
  }, [openItems, search])

  const selectedItems = openItems.filter((oi) => selected.has(oi.apx_id))
  const selectedTotal = selectedItems.reduce((sum, oi) => sum + Math.abs(parseFloat(oi.balance) || 0), 0)
  const txnAmount = Math.abs(transaction.amount)
  const isOutgoing = transaction.amount < 0
  const diff = Math.abs(selectedTotal - txnAmount)
  const isExactMatch = diff < 0.01

  const toggleItem = (apxId: number) => {
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(apxId)) next.delete(apxId)
      else next.add(apxId)
      return next
    })
  }

  // Build a fake Match object from selected open items for CounterpartEditor
  const pseudoMatch = useMemo(() => {
    if (selectedItems.length === 0) return null

    const counterparts: Counterpart[] = selectedItems.map((oi) => {
      const amount = Math.min(Math.abs(parseFloat(oi.balance) || 0), txnAmount)
      return {
        id: `temp-${oi.apx_id}`,
        counterpart_kind: 'mirage_open_item',
        dt_account_id: isOutgoing ? oi.account_id : bankAccountId,
        ct_account_id: isOutgoing ? bankAccountId : oi.account_id,
        dt_apx_id: isOutgoing ? oi.apx_id : 0,
        ct_apx_id: isOutgoing ? 0 : oi.apx_id,
        amount: amount.toFixed(4),
        note: transaction.description?.slice(0, 200),
        doc_nom: oi.doc_number || undefined,
        doc_date: oi.doc_date || undefined,
      }
    })

    return {
      id: '',
      transaction_id: transaction.id,
      state: 'unmatched' as const,
      match_kind: (counterparts.length > 1 ? 'split' : 'invoice_payment') as 'invoice_payment' | 'split',
      mirage_bank_account_id: bankAccountId,
      counterparts,
      created_at: new Date().toISOString(),
    }
  }, [selectedItems, txnAmount, isOutgoing, bankAccountId, transaction])

  const handleConfirm = (counterparts: CounterpartPayload[]) => {
    createMatch.mutate({
      transaction_id: transaction.id,
      client_id: clientId,
      mirage_bank_account_id: bankAccountId,
      match_kind: counterparts.length > 1 ? 'split' : 'invoice_payment',
      counterparts,
    })
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8 text-neutral-400">
        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
        Loading open items...
      </div>
    )
  }

  return (
    <div className="space-y-3">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-neutral-400" />
        <Input
          placeholder="Search by counterparty, doc number, account..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="h-8 pl-8 text-sm"
        />
      </div>

      <div className="max-h-[200px] overflow-y-auto rounded-md border border-neutral-200">
        {filtered.length === 0 ? (
          <div className="p-4 text-center text-sm text-neutral-400">
            No open items found.
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="sticky top-0 border-b border-neutral-200 bg-neutral-50 text-[11px] uppercase tracking-wide text-neutral-400">
              <tr>
                <th className="w-8 px-2 py-1.5" />
                <th className="px-2 py-1.5 text-left font-medium">Account</th>
                <th className="px-2 py-1.5 text-left font-medium">Counterparty / Doc</th>
                <th className="px-2 py-1.5 text-right font-medium">Balance</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-100">
              {filtered.map((oi) => {
                const bal = parseFloat(oi.balance)
                const hasDetail = oi.counterparty_name || oi.doc_number
                return (
                  <tr
                    key={oi.apx_id}
                    className={cn(
                      'cursor-pointer transition-colors hover:bg-blue-50/60',
                      selected.has(oi.apx_id) && 'bg-blue-50'
                    )}
                    onClick={() => toggleItem(oi.apx_id)}
                  >
                    <td
                      className="px-2 py-1.5 text-center"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <Checkbox
                        checked={selected.has(oi.apx_id)}
                        onCheckedChange={() => toggleItem(oi.apx_id)}
                      />
                    </td>
                    <td className="px-2 py-1.5">
                      <span className="font-mono text-xs font-medium text-neutral-700">{oi.account_code}</span>
                      <span className="ml-1.5 text-neutral-400">{oi.account_name}</span>
                    </td>
                    <td className="px-2 py-1.5 text-neutral-600">
                      {hasDetail ? (
                        <span>
                          {oi.counterparty_name && <span className="font-medium text-neutral-700">{oi.counterparty_name}</span>}
                          {oi.counterparty_name && oi.doc_number && <span className="mx-1 text-neutral-300">·</span>}
                          {oi.doc_number && <span className="text-neutral-500">#{oi.doc_number}</span>}
                          {oi.doc_date && <span className="ml-1.5 text-neutral-400">{oi.doc_date}</span>}
                        </span>
                      ) : (
                        <span className="text-neutral-300">—</span>
                      )}
                    </td>
                    <td className="px-2 py-1.5 text-right">
                      <span className={cn(
                        'font-mono font-medium',
                        bal >= 0 ? 'text-green-700' : 'text-red-600'
                      )}>
                        {formatCurrency(oi.balance, transaction.currency)}
                      </span>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Selection summary */}
      {selected.size > 0 && (
        <div className="space-y-3">
          <div className="flex items-center gap-3 rounded-md border border-neutral-200 bg-neutral-50 px-3 py-2 text-sm">
            <span className="text-neutral-500">
              {selected.size} item{selected.size !== 1 && 's'} selected
            </span>
            <span className="text-neutral-300">·</span>
            <span className="font-mono font-medium text-neutral-700">{formatCurrency(selectedTotal, transaction.currency)}</span>
            <span className="text-neutral-300">of</span>
            <span className="font-mono font-medium text-neutral-700">{formatCurrency(txnAmount, transaction.currency)}</span>
            {!isExactMatch && (
              <>
                <span className="text-neutral-300">·</span>
                <span className="font-mono text-amber-600">
                  {selectedTotal > txnAmount ? '+' : '−'}{formatCurrency(diff, transaction.currency)}
                </span>
              </>
            )}
            {isExactMatch && (
              <>
                <span className="text-neutral-300">·</span>
                <span className="text-green-600">✓ exact match</span>
              </>
            )}
          </div>

          {/* Editable counterpart form pre-filled from selection */}
          {pseudoMatch && (
            <CounterpartEditor
              match={pseudoMatch}
              transaction={transaction}
              clientId={clientId}
              bankAccountId={bankAccountId}
              bankAccountLabel={bankAccountLabel}
              onConfirm={handleConfirm}
              hideSave
            />
          )}

          <div className="flex items-center gap-2">
            <Button
              size="sm"
              variant="blue"
              className="gap-1.5"
              disabled={createMatch.isLoading}
              onClick={() => pseudoMatch && handleConfirm(
                pseudoMatch.counterparts.map((cp) => ({
                  counterpart_kind: cp.counterpart_kind,
                  dt_account_id: cp.dt_account_id,
                  ct_account_id: cp.ct_account_id,
                  dt_apx_id: cp.dt_apx_id,
                  ct_apx_id: cp.ct_apx_id,
                  amount: cp.amount,
                  note: cp.note,
                  doc_nom: cp.doc_nom,
                  doc_date: cp.doc_date,
                }))
              )}
            >
              <Check className="h-3.5 w-3.5" />
              {createMatch.isLoading ? 'Creating match...' : 'Confirm match'}
            </Button>
            {createMatch.isError && (
              <span className="text-xs text-red-600">
                {(createMatch.error as Error).message}
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
